import { UseQueryResult, useQueries } from "@tanstack/react-query";
import { chunk } from "lodash";

import { getCarbonProgramOutcomes } from "services";

import { getActiveToken, persistentQueryClient, useUserContext } from "utils";

import { GET_CARBON_PROGRAM_OUTCOMES, GET_FIELDS, PROGRAM_OUTCOME_CHUNK_SIZE } from "consts";

import { CarbonProgram, CarbonProgramOutcomesResponse, FieldListField } from "models";

type UseGetCarbonProgramOutcomesReturnType = Pick<
  UseQueryResult<CarbonProgramOutcomesResponse>,
  "data" | "isFetching" | "isLoading" | "isError" | "isLoadingError"
> & {
  progress: number;
};

export const useGetAllCarbonProgramOutcomes = (
  farmId: string | undefined,
  carbonProgram: CarbonProgram | null | undefined
): UseGetCarbonProgramOutcomesReturnType => {
  const queryClient = persistentQueryClient;

  const { user } = useUserContext();
  const token = getActiveToken();

  const queryState = queryClient.getQueryState<FieldListField[]>([GET_FIELDS, farmId]);
  const fields = queryState?.data ?? [];

  const sortFields = (a: FieldListField, b: FieldListField) => b.size - a.size;

  const fieldIds = fields?.toSorted(sortFields).map(({ id }) => id) ?? [];
  const chunks = chunk(fieldIds, PROGRAM_OUTCOME_CHUNK_SIZE);

  const queries = chunks.map((chunk) => ({
    queryKey: [GET_CARBON_PROGRAM_OUTCOMES, chunk],
    queryFn: () => getCarbonProgramOutcomes({ fieldIds: chunk }),
    enabled: !!farmId && !!carbonProgram && !!user && !!token, // only fetch if user is logged in + extra conditions met
    staleTime: 1000 * 60 * 60 * 24 * 7 * 2, // 2 weeks
  }));

  return useQueries({
    queries,
    combine: combineQueries(!!carbonProgram, chunks.length),
  });
};

export const combineQueries = (enabled: boolean, chunkSize: number) => (results: UseQueryResult<CarbonProgramOutcomesResponse, Error>[]) => ({
  data: results.reduce((acc, result) => ({ ...acc, ...result.data }), {}),
  enabled,
  isLoading: results.some((result) => !!result.isLoading),
  isFetching: results.some((result) => !!result.isFetching),
  isError: results.some((result) => !!result.isError),
  isLoadingError: results.some((result) => !!result.isLoadingError),
  progress: !results.some((result) => !!result.isFetching) ? 100 : Math.ceil((results.filter((result) => !result.isFetching).length / chunkSize) * 100),
});
